import { useState } from 'react'
import { Input, Select } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { BillingAddress, BillingCountry, UpdateProfAccountRequest } from '@/models/billingAddress.ts'

interface BillingAddressFormProps {
  initialData?: Partial<BillingAddress>
  onSubmit: (data: UpdateProfAccountRequest) => Promise<void>
  submitLabel?: string
  isLoading?: boolean
}

const countryOptions: { value: BillingCountry; label: string }[] = [
  { value: 'DE', label: 'Deutschland' },
  { value: 'AT', label: 'Österreich' },
  { value: 'CH', label: 'Schweiz' },
]

export default function BillingAddressForm({
  initialData,
  onSubmit,
  submitLabel = 'Speichern',
  isLoading = false,
}: BillingAddressFormProps) {
  const [formData, setFormData] = useState<UpdateProfAccountRequest>({
    billing_company: initialData?.billing_company || '',
    billing_vat_id: initialData?.billing_vat_id || '',
    billing_country: initialData?.billing_country || 'DE',
    billing_first_name: initialData?.billing_first_name || '',
    billing_last_name: initialData?.billing_last_name || '',
    billing_street: initialData?.billing_street || '',
    billing_postal: initialData?.billing_postal || '',
    billing_city: initialData?.billing_city || '',
  })

  function handleChange(field: keyof UpdateProfAccountRequest, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Vorname <span className="text-error">*</span></span>
          </div>
          <Input
            value={formData.billing_first_name}
            onChange={e => handleChange('billing_first_name', e.target.value)}
            required
          />
        </label>

        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Nachname <span className="text-error">*</span></span>
          </div>
          <Input
            value={formData.billing_last_name}
            onChange={e => handleChange('billing_last_name', e.target.value)}
            required
          />
        </label>
      </div>

      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Firma</span>
        </div>
        <Input
          value={formData.billing_company}
          onChange={e => handleChange('billing_company', e.target.value)}
        />
      </label>

      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">USt-IdNr.</span>
        </div>
        <Input
          value={formData.billing_vat_id}
          onChange={e => handleChange('billing_vat_id', e.target.value)}
          placeholder="z.B. DE123456789"
        />
      </label>

      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Straße und Hausnummer <span className="text-error">*</span></span>
        </div>
        <Input
          value={formData.billing_street}
          onChange={e => handleChange('billing_street', e.target.value)}
          required
        />
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">PLZ <span className="text-error">*</span></span>
          </div>
          <Input
            value={formData.billing_postal}
            onChange={e => handleChange('billing_postal', e.target.value)}
            required
          />
        </label>

        <label className="form-control w-full md:col-span-2">
          <div className="label">
            <span className="label-text">Stadt <span className="text-error">*</span></span>
          </div>
          <Input
            value={formData.billing_city}
            onChange={e => handleChange('billing_city', e.target.value)}
            required
          />
        </label>
      </div>

      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Land <span className="text-error">*</span></span>
        </div>
        {/* @ts-ignore */}
        <Select
          value={formData.billing_country}
          onChange={e => handleChange('billing_country', e.currentTarget.value as BillingCountry)}
          required
        >
          {countryOptions.map(opt => (
            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
          ))}
        </Select>
      </label>

      <Button type="submit" color="primary" loading={isLoading} disabled={isLoading}>
        {submitLabel}
      </Button>
    </form>
  )
}
